import store from "store";
import pusherService from "./pusherService";
import {
  createSmoothedGenerationProgress,
  mapRecordGenerationProgressMessage,
  mapRecordGenerationProgressPercent,
  PROGRESS_IDLE_BUMP_MS,
} from "./recordGenerationProgress";
import { performRefresh } from "auth/refreshSession";

const DEFAULT_WAIT_MS = 30 * 60 * 1000;
const COMPLETION_POLL_INTERVAL_MS = 5000;
const LOG = "[FinalizeFlow]";

function ensurePusherConnected() {
  const user = store.getState()?.auth?.user;
  const accountId = Number(user?.accountId);
  const userId = Number(user?.userId ?? user?.id);
  if (accountId && userId) {
    pusherService.connect(accountId, userId);
    console.log(LOG, "Pusher connect for record generation", {
      channel: `${accountId}-${userId}`,
    });
  } else {
    console.warn(LOG, "Pusher connect skipped — missing auth user", {
      accountId,
      userId,
    });
  }
}

const isAxiosTimeout = (error) =>
  error?.code === "ECONNABORTED" ||
  String(error?.message || "").toLowerCase().includes("timeout");

async function refreshScreenAfterGeneration() {
  try {
    await performRefresh();
  } catch (refreshError) {
    console.error(
      LOG,
      "Session refresh after declaration generation failed:",
      refreshError,
    );
  }
  console.log(LOG, "Reloading page after declaration generation");
  window.location.reload();
}

export function attachRecordGenerationWaiting({
  orderId,
  onProgress,
  onCompleted,
  onFailed,
  timeoutMs = DEFAULT_WAIT_MS,
}) {
  ensurePusherConnected();

  let settled = false;
  let timeoutId = null;
  let unsubProgress = null;
  let unsubFileReady = null;

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    unsubProgress?.();
    unsubProgress = null;
    unsubFileReady?.();
    unsubFileReady = null;
  };

  const settle = (fn, value) => {
    if (settled) return;
    settled = true;
    cleanup();
    fn(value);
  };

  const matchesOrder = (payload) =>
    Number(payload?.orderId) === Number(orderId);

  const handleRecordProgress = (payload) => {
    console.log(LOG, "Pusher record-generation-progress (raw)", payload);

    if (!matchesOrder(payload)) {
      console.log(LOG, "Pusher record-generation-progress ignored (orderId)", {
        expected: orderId,
        received: payload?.orderId,
      });
      return;
    }

    console.log(LOG, "Pusher record-generation-progress (matched)", payload);

    onProgress?.({
      ...payload,
      progress: {
        ...(payload?.progress || {}),
        percentage: mapRecordGenerationProgressPercent(payload),
      },
    });

    const step = String(payload?.step || "").toUpperCase();

    if (step === "COMPLETED") {
      settle(onCompleted, payload);
    } else if (step === "FAILED") {
      settle(onFailed, {
        message:
          payload?.error ||
          payload?.message ||
          "Failed to generate declaration of records.",
        payload,
      });
    }
  };

  const handleFileReady = (payload) => {
    if (!matchesOrder(payload)) return;

    const status = String(payload?.status || "").toUpperCase();

    if (status === "READY") {
      console.log(LOG, "Pusher file-ready (declaration complete)", payload);
      onProgress?.({
        orderId,
        step: "COMPLETED",
        message: "Declaration of records is ready.",
        progress: { percentage: 100, current: 100, total: 100 },
      });
      settle(onCompleted, payload);
    } else if (status === "FAILED") {
      settle(onFailed, {
        message: "Failed to generate declaration of records.",
        payload,
      });
    }
  };

  unsubProgress = pusherService.subscribe(
    "record-generation-progress",
    handleRecordProgress,
  );
  unsubFileReady = pusherService.subscribe("file-ready", handleFileReady);

  timeoutId = setTimeout(() => {
    settle(onFailed, {
      message:
        "Declaration generation timed out. Please refresh and try again.",
      payload: null,
    });
  }, timeoutMs);

  return cleanup;
}

export async function waitForRecordGenerationCompletion({
  orderId,
  requestGeneratePdf,
  onProgress,
  timeoutMs = DEFAULT_WAIT_MS,
}) {
  if (typeof requestGeneratePdf === "function") {
    return generatePdfAndWaitForDeclaration({
      orderId,
      requestGeneratePdf,
      onProgress,
      timeoutMs,
      refreshOnComplete: true,
    });
  }

  const progressSession = createSmoothedGenerationProgress(onProgress);
  progressSession.start();

  let settled = false;
  const idleBumpId = setInterval(() => {
    if (!settled) progressSession.bumpTarget();
  }, PROGRESS_IDLE_BUMP_MS);

  return new Promise((resolve, reject) => {
    attachRecordGenerationWaiting({
      orderId,
      onProgress: (payload) => {
        if (settled) return;
        progressSession.push(payload);
      },
      timeoutMs,
      onCompleted: async (payload) => {
        if (settled) return;
        settled = true;
        clearInterval(idleBumpId);

        try {
          await progressSession.complete(
            mapRecordGenerationProgressMessage(payload) ||
              "Declaration of records is ready.",
          );
        } finally {
          progressSession.stop();
        }

        await refreshScreenAfterGeneration();
        resolve(payload);
      },
      onFailed: ({ message }) => {
        if (settled) return;
        settled = true;
        clearInterval(idleBumpId);
        progressSession.stop();
        reject(new Error(message));
      },
    });
  });
}

export async function generatePdfAndWaitForDeclaration({
  orderId,
  requestGeneratePdf,
  onProgress,
  timeoutMs = DEFAULT_WAIT_MS,
  refreshOnComplete = true,
  pollForCompletion,
}) {
  const progressSession = createSmoothedGenerationProgress(onProgress);
  progressSession.start();

  let cleanup = null;
  let settled = false;
  const idleBumpId = setInterval(() => {
    if (!settled) progressSession.bumpTarget();
  }, PROGRESS_IDLE_BUMP_MS);

  const waitPromise = new Promise((resolve, reject) => {
    cleanup = attachRecordGenerationWaiting({
      orderId,
      onProgress: (payload) => {
        if (settled) return;
        progressSession.push(payload);
      },
      timeoutMs,
      onCompleted: (payload) => resolve({ kind: "completed", payload }),
      onFailed: ({ message }) =>
        reject(
          Object.assign(new Error(message), { __pusherFailure: true }),
        ),
    });
  });

  waitPromise.catch(() => {});

  const apiStartedAt = Date.now();
  let stopPolling = false;

  try {
    console.log(LOG, "generatePDF API request start", { orderId });

    try {
      await requestGeneratePdf();
      console.log(
        LOG,
        "generatePDF API request finished (awaiting Pusher completion)",
        {
          orderId,
          apiElapsedMs: Date.now() - apiStartedAt,
        },
      );
    } catch (error) {
      if (!isAxiosTimeout(error)) {
        console.log(LOG, "generatePDF API request failed hard", {
          orderId,
          message: error?.message,
        });
        cleanup?.();
        throw error;
      }

      console.log(
        LOG,
        "generatePDF API request timeout — waiting on Pusher",
        {
          orderId,
          message: error?.message,
        },
      );
      progressSession.push({
        orderId,
        step: "PROGRESS",
        message:
          "Request timed out; still waiting for declaration generation on the server…",
        progress: { percentage: 0, current: 0, total: 100 },
      });
    }

    const pollingPromise =
      typeof pollForCompletion === "function"
        ? (async () => {
            while (!stopPolling) {
              try {
                const payload = await pollForCompletion();
                if (payload) {
                  console.info(
                    LOG,
                    "Declaration detected by completion polling",
                    { orderId },
                  );
                  return {
                    kind: "completed",
                    payload:
                      payload === true
                        ? {
                            orderId,
                            step: "COMPLETED",
                            message: "Declaration of records is ready.",
                          }
                        : payload,
                  };
                }
              } catch (error) {
                console.warn(
                  LOG,
                  "Declaration completion polling failed; will retry",
                  { orderId, message: error?.message },
                );
              }

              await new Promise((resolve) =>
                setTimeout(resolve, COMPLETION_POLL_INTERVAL_MS),
              );
            }

            return null;
          })()
        : null;

    const outcome = pollingPromise
      ? await Promise.race([
          waitPromise,
          pollingPromise.then((result) => result || waitPromise),
        ])
      : await waitPromise;
    settled = true;
    await progressSession.complete(
      mapRecordGenerationProgressMessage(outcome.payload) ||
        "Declaration of records is ready.",
    );
    if (refreshOnComplete) {
      await refreshScreenAfterGeneration();
    }
    return outcome.payload;
  } finally {
    settled = true;
    stopPolling = true;
    cleanup?.();
    clearInterval(idleBumpId);
    progressSession.stop();
  }
}

export { isAxiosTimeout };